import { getCookie } from '@avalara/bat-apps-shared/helpers/cookies';
import { isPaidAvataxObligation } from '@avalara/bat-apps-shared/helpers/obligations/avatax';
import type { Result } from '@avalara/types/index';
import type { RouteComponentProps } from 'react-router-dom';
import type { APAccountInfo } from '../../../../types/marketing';

import { EMEA_RECORD_TYPE_ID } from '@avalara/bat-apps-shared/constants';
import { getReturnsEligibility } from '../../../shared/eligibility';
import { formatPrice } from '../../../shared/helpers';
import { getEsbConnectorUrl } from '../../components/helpers/redirect';
import conf from '../../conf';
import {
  getAnalyticsByActiveFlow,
  getOfferByActiveFlow,
  getOfferMetadataByActiveFlow,
} from '../../reducers/aggregate-logic';
import { getOrderTotal, getSelectedPlan, getValidatedConnectorId } from '../../selectors/order';
import {
  createAvataxAccountSuccess,
  receiveAccountInitializationDetails,
  setBuySuccess,
} from '../../store/avatax-signup';
import { loadingDataStart } from '../../store/data';
import { fillSharedForm } from '../../store/shared-signup';
import {
  openPollingDialog,
  setAccountFetchFailed,
  setNeedToFetchCybersourceData,
  setSystemError,
} from '../../store/system';
import { receiveUser, setAccountSetupRetryKey } from '../actions';
import analytics, { scrubPostBody } from '../analytics';
import { formatAccountSetupError } from '../errors';
import fetch from '../fetch';
import { getAuthLogin, getWindowQueryUnlessInProd, openExternalLink } from '../helpers';
import localStorage from '../local-storage';
import { trackOfferPurchaseAnalytics, updateProductAnalytics } from './analytics';
import { getPaymentMethods } from './biztech';
import { getSystemErrorMessaging } from './error';
import { authRedirect, basePostParams } from './index';
import { goToNextOfferStep } from './offer-flow';

import type { Dispatch, ThunkAction } from '@avalara/types/actions';
import type { InitialAddObligationsOptions, NewAccountForm } from '@avalara/types/index';
import type { State } from '@avalara/types/reducer-states';
import type {
  AccountSetupDetails,
  ActiveFlow,
  CreateAccountClientSideResponse,
  CreateAccountRequest,
  CreateObapiAccountRequest,
  CustomerAccountSearchRequest,
  CustomerAccountSearchResponse,
  EmailExistsResponse,
  GetAccountResponse,
  SetUpAccountErrorDetails,
  UnauthenticatedUser,
} from '../../../../types';
import type { CreateAccountForOfferRequest } from '../../../../types/offers';

const displayEmailExistsMessage = (productName: string) => (dispatch: Dispatch) => {
  dispatch(getSystemErrorMessaging({ errorCode: 'emailAlreadyExists', productName }));
};

const displayAccountExistsMessage = (locationString: string) => (dispatch: Dispatch) => {
  dispatch(getSystemErrorMessaging({ errorCode: 'accountAlreadyExists', locationString }));
};

const createAccountInfo = (newAccountForm: NewAccountForm) => {
  // NOTE (Sasha): set postal code to none only if RSB net new and international
  const isUnitedStatesOrCanada = newAccountForm.country === 'US' || newAccountForm.country === 'CA';
  const defaultPostalCode =
    isUnitedStatesOrCanada || newAccountForm.postalCode !== '' ? newAccountForm.postalCode : 'none';

  return {
    accountName: newAccountForm.companyName,
    firstName: newAccountForm.firstName.trim(),
    lastName: newAccountForm.lastName,
    email: newAccountForm.email,
    phone: newAccountForm.phone,
    companyAddress: {
      line: [newAccountForm.street1, newAccountForm.street2].filter(Boolean).join(' '),
      region: newAccountForm.state,
      postalCode: defaultPostalCode,
      city: newAccountForm.city,
      country: newAccountForm.country,
    },
    emeaCompanyType: newAccountForm.emeaCompanyType,
    vatNumber: newAccountForm.vatNumber,
    isAdmin: false,
  };
};

export const checkExistingUserEmail = async (dispatch: Dispatch, email?: string): Promise<boolean> => {
  if (!email) return false;

  const result = await fetch(dispatch, { showLoadingWheel: false })<EmailExistsResponse>(
    `${conf.API_URL}/api/accounts/email/exists?email=${encodeURIComponent(email)}`,
    {
      method: 'GET',
    },
  );

  return result.ok ? result.value.exists : false;
};

// Free-trial eligibility: looks up the company by name + address via the IPS
// customer-accounts/search endpoint (BRGUI-2103). A non-empty result means the
// company already has an Avalara account, so the self-serve free trial is not
// available. Fails open (returns false) if the lookup itself errors, since the
// authoritative enforcement still happens server-side at account creation.
export const checkExistingCustomerAccount = async (
  dispatch: Dispatch,
  searchRequest: CustomerAccountSearchRequest,
): Promise<boolean> => {
  const { name, street, city, state, country, postalCode } = searchRequest;
  if (!name || !street || !city || !state || !country || !postalCode) return false;

  const result = await fetch(dispatch)<CustomerAccountSearchResponse>(`${conf.API_URL}/api/accounts/search`, {
    ...basePostParams,
    body: JSON.stringify(searchRequest),
  });

  return result.ok ? result.value.length > 0 : false;
};

// We make marketing updates dynamically based on whether we're dealing with an AvaTax or BLL signup
const createAccount = async (
  dispatch: Dispatch,
  state: State,
  paymentToken: string | null | undefined,
): Promise<Result<CreateAccountClientSideResponse>> => {
  const {
    sharedSignup: { signupForm },
    data: { marketingData },
    system: { activeFlow },
    order: { discountCode, salesTax },
  } = state;

  const postBody: CreateAccountRequest = {
    activeFlow,
    offer: getOfferByActiveFlow(state, paymentToken),
    marketingData,
    paymentMethodId: paymentToken,
    discountCode,
    accountInfo: createAccountInfo(signupForm),
    salesTax,
  };

  const postParams = {
    ...basePostParams,
    body: JSON.stringify(postBody),
  };

  const result: Result<CreateAccountClientSideResponse> = await fetch(dispatch)(
    `${conf.API_URL}/api/accounts`,
    postParams,
  );

  if (result.ok) {
    dispatch(createAvataxAccountSuccess(Number.parseInt(result.value.accountId, 10)));
  } else {
    analytics.track('SIGNUP_FAILURE', {
      message: result.message,
      status: result.status,
      body: scrubPostBody(postBody),
      error: true,
    });
  }

  return result;
};

type HandleCreateAccountErrorParams = {
  resultMessage: string;
  productName: string;
  isPaid: boolean;
};

const handleCreateAccountError =
  ({ resultMessage, productName, isPaid }: HandleCreateAccountErrorParams) =>
  async (dispatch: Dispatch) => {
    if (/email already exists/.test(resultMessage)) {
      dispatch(displayEmailExistsMessage(productName));
    } else if (/account already exists/.test(resultMessage)) {
      dispatch(displayAccountExistsMessage(getAuthLogin()));
    } else {
      dispatch(
        getSystemErrorMessaging({
          errorCode: 'signupError',
          msg: `We were unable to set up your ${productName}.`,
          serverMsg: resultMessage,
          isPaid,
        }),
      );
    }

    if (!isPaid) return;

    // NOTE: Need to refetch cybersource data on error
    dispatch(setNeedToFetchCybersourceData(true));
  };

export const createAccountWithOffer =
  (history: RouteComponentProps['history'], paymentMethodId?: string) =>
  async (dispatch: Dispatch, getState: () => State) => {
    const state = getState();
    const {
      account: { retryKey, isAdmin },
      data: { marketingData },
      offer: { deliverySystemToPollFor, isEmeaOffer, isFreeTrial, offerFlow, offerName },
      order: { discountCode, offerSpecificSettings, quantity, selectedPlanName },
      sharedSignup: { signupForm },
    } = state;

    const selectedPlan = getSelectedPlan(state);
    if (!offerFlow || !selectedPlanName || !selectedPlan) {
      // TODO(Josh): dispatch error modal
      return;
    }

    const { currency } = selectedPlan;

    const postBody: CreateAccountForOfferRequest = {
      accountInfo: {
        ...createAccountInfo(signupForm),
        currency,
        recordTypeId: isEmeaOffer ? EMEA_RECORD_TYPE_ID : undefined,
        userPassword: signupForm.password,
        isAdmin,
      },
      connectorId: getValidatedConnectorId(state),
      discountCode,
      marketingData,
      offerFlow,
      offerSpecificSettings,
      paymentMethodId,
      quantity,
      retryKey,
      selectedPlanName,
      totalCost: formatPrice(getOrderTotal(state), 2, currency),
    };

    const postParams = {
      ...basePostParams,
      body: JSON.stringify(postBody),
    };

    const result = await fetch(dispatch)<AccountSetupDetails, SetUpAccountErrorDetails>(
      `${conf.API_URL}/api/accounts/v2${window.location.search}`,
      postParams,
    );

    if (!result.ok) {
      analytics.track('SIGNUP_FAILURE', {
        message: result.message,
        status: result.status,
        body: scrubPostBody(postBody),
        error: true,
      });

      const newRetryKey = result.details?.retryKey;
      if (newRetryKey) dispatch(setAccountSetupRetryKey(newRetryKey));

      const setupErrorObj = formatAccountSetupError({
        isFreeTrial,
        resultMessage: result.message,
        // For ELR/DBNA, existing-email should *prompt* sign-in (no auto redirect on dismissal)
        redirectUrl: offerFlow === 'elr' || offerFlow === 'dbna' ? `/${offerFlow}` : `/${offerFlow}?login=true`,
        passwordRedirectUrl: `/${offerFlow}/get-started?invalidPassword=true`,
        productName: offerName,
        signInUrl:
          offerFlow === 'elr' || offerFlow === 'dbna'
            ? `${conf.CLIENT_URL}/auth/login?returnUrl=${encodeURIComponent(
                `${conf.BUY_CLIENT_URL}/${offerFlow}/activate${window.location.search}`,
              )}`
            : undefined,
      });

      const { html, message, title, locationAfterErrorDismissal } = setupErrorObj;

      if (message || html) {
        dispatch(
          setSystemError({
            html,
            message,
            title,
            secondaryMessage: [],
            locationAfterErrorDismissal,
          }),
        );
      } else {
        dispatch(
          handleCreateAccountError({
            resultMessage: result.message,
            productName: offerName,
            isPaid: true,
          }),
        );
      }
      return;
    }

    const { accountId, provisioningStatusId, magicLink } = result.value;

    // TODO: Do we need to dispatch createAvataxAccountSuccess for all flows,
    // or just for AvaTax, and for other flows update account.accountId?
    dispatch(createAvataxAccountSuccess(accountId));

    if (magicLink) dispatch(receiveAccountInitializationDetails(result.value));
    dispatch(trackOfferPurchaseAnalytics(offerFlow, accountId, true));

    if (deliverySystemToPollFor && provisioningStatusId) {
      dispatch(openPollingDialog(provisioningStatusId));
    } else {
      dispatch(goToNextOfferStep({ history }));
    }
  };

export const createNetNewSTRAAccount =
  ({ paymentToken, history, handleError = () => {} }: InitialAddObligationsOptions) =>
  async (dispatch: Dispatch, getState: () => State) => {
    const {
      system: { activeFlow },
    } = getState();
    if (!activeFlow) return;

    // NOTE(Sasha): Set paymentToken to undefined to successfully add a 100% discounted offer
    // Setting paymentToken to an empty string causes a Validation error from OPUS
    const result = await createAccount(dispatch, getState(), paymentToken || undefined);

    if (!result.ok) {
      dispatch(
        handleCreateAccountError({
          resultMessage: result.message || '',
          productName: 'Sales Tax Risk Assessment',
          isPaid: true,
        }),
      );

      // TODO: refactor out this handleError function in light of above error handler
      handleError();
      return;
    }

    dispatch(updateProductAnalytics('purchase'));

    const { eventName, data } = getAnalyticsByActiveFlow(getState());
    analytics.track(eventName, data);
    history.push('/risk-assessment/thank-you');
  };

export const createAvataxPaidAccount =
  ({ paymentToken, history, handleError = () => {} }: InitialAddObligationsOptions) =>
  async (dispatch: Dispatch, getState: () => State) => {
    if (getCookie('TESTCAFE_E2E')) {
      history.push('/buy/success');
      return;
    }
    const result = await createAccount(dispatch, getState(), paymentToken);

    if (!result.ok) {
      if (/email already exists/.test(result.message || '')) {
        dispatch(displayEmailExistsMessage('trial'));
      } else if (/account already exists/.test(result.message || '')) {
        dispatch(displayAccountExistsMessage(`${conf.AA_URL}`));
      } else {
        dispatch(
          getSystemErrorMessaging({
            errorCode: 'signupError',
            msg: 'Failed to provision paid tier.',
            serverMsg: result.message,
            isPaid: true,
          }),
        );
        handleError();
      }
      history.push('/buy/company');
      return;
    }

    dispatch(setBuySuccess());
    localStorage.set(`paidAvataxOrBundlePurchase-${result.value.accountId}`, 'true');

    const { eventName, data } = getAnalyticsByActiveFlow(getState());
    analytics.track(eventName, data);
    history.push('/buy/success');
  };

const getStoredConnector = (accountId: number, getState: () => State) => {
  if (!window.localStorage) return undefined;

  const storedConnectorId = window.localStorage.getItem(`ava-connector-id-signup-for-${accountId}`);
  if (!storedConnectorId) return undefined;

  const {
    data: { connectors },
  } = getState();
  return connectors.data.find((c) => c.id === storedConnectorId);
};

const isUnauthenticatedUserResultValue = (resultValue: GetAccountResponse): resultValue is UnauthenticatedUser =>
  (resultValue as UnauthenticatedUser).unauthenticatedUser === true;

/** SessionStorage key used to avoid redirect loop when server keeps returning unauthenticatedUser. */
const AUTH_REDIRECT_LOOP_GUARD_KEY = 'buydot-unauthenticated-redirect-done';

/** If we already redirected once for unauthenticated, show error instead of redirecting again. */
function handleUnauthenticatedRedirect(dispatch: Dispatch, doRedirect: boolean): void {
  if (!doRedirect) return;
  dispatch(loadingDataStart());
  try {
    if (sessionStorage.getItem(AUTH_REDIRECT_LOOP_GUARD_KEY)) {
      dispatch(getSystemErrorMessaging({ errorCode: 'failedAccountFetch' }));
      return;
    }
    sessionStorage.setItem(AUTH_REDIRECT_LOOP_GUARD_KEY, '1');
    setTimeout(() => authRedirect(), 1000);
  } catch {
    setTimeout(() => authRedirect(), 1000);
  }
}

/** On offer routes show error only (redirect would loop). On non-offer routes redirect to login. */
function handleUnauthenticatedUser(
  dispatch: Dispatch,
  getState: () => State,
  errorCode: 'failedAccountFetch' | 'ineligibleForAccountsPayable',
): void {
  const state = getState();
  if (state.offer?.offerFlow) {
    dispatch(setAccountFetchFailed(true));
    dispatch(getSystemErrorMessaging({ errorCode }));
    return;
  }
  handleUnauthenticatedRedirect(dispatch, true);
}

export function clearAuthRedirectLoopGuard(): void {
  try {
    sessionStorage.removeItem(AUTH_REDIRECT_LOOP_GUARD_KEY);
  } catch {
    // ignore
  }
}

const fetchAccount = (dispatch: Dispatch) => {
  const query = getWindowQueryUnlessInProd();
  return fetch(dispatch)<GetAccountResponse>(`${conf.API_URL}/api/accounts${query}`);
};

export const fetchAccountData =
  (): ThunkAction =>
  async (dispatch: Dispatch): Promise<void> => {
    const result = await fetchAccount(dispatch);

    if (!result.ok) {
      dispatch(getSystemErrorMessaging({ errorCode: 'failedAccountFetch', serverMsg: result.message || undefined }));
      return;
    }

    if (isUnauthenticatedUserResultValue(result.value)) {
      handleUnauthenticatedRedirect(dispatch, true);
      return;
    }

    clearAuthRedirectLoopGuard();
    dispatch(receiveUser({ ...result.value }));
  };

export const fetchAccountForService =
  (): ThunkAction =>
  async (dispatch: Dispatch, getState: () => State): Promise<void> => {
    // Fetch account first so we can detect "no Avatax account" before triggering
    // getPaymentMethods or any other side effects that may cause AUI/host to refetch or reload.
    const result = await fetchAccount(dispatch);

    if (!result.ok) {
      const state = getState();
      if (state.system.activeFlow === 'esb-trial') {
        const esbConnectorUrl = getEsbConnectorUrl({
          connectorReturnUrl: state.esb.connectorReturnUrl,
          userExists: true,
          error: true,
        });
        openExternalLink(esbConnectorUrl);
        return;
      }
      if (state.offer.offerFlow) {
        dispatch(setAccountFetchFailed(true));
      }
      dispatch(
        getSystemErrorMessaging({
          errorCode: 'signupError',
          msg: 'We failed to retrieve your account. Please try logging out and logging back in again.',
          serverMsg: result.message,
          isPaid: false,
        }),
      );
      return;
    }

    if (isUnauthenticatedUserResultValue(result.value)) {
      // User has Identity session but no Avatax account (accountId 0, no obligations).
      // Show error dialog and stop retries; do not call getPaymentMethods or other fetches.
      dispatch(setAccountFetchFailed(true));
      dispatch(getSystemErrorMessaging({ errorCode: 'failedAccountFetch' }));
      return;
    }

    // We have a valid Avatax account; now safe to fetch payment methods and continue.
    dispatch(getPaymentMethods());

    const { accountId, billingContact, companyName, userId } = result.value;

    clearAuthRedirectLoopGuard();
    dispatch(setAccountFetchFailed(false));
    analytics.identify(userId, { accountId });

    const storedConnector = getStoredConnector(accountId, getState);
    dispatch(receiveUser({ ...result.value, storedConnector }));

    if (billingContact !== 'ERROR') dispatch(fillSharedForm({ billingContact, companyName }));
  };

export const fetchAccountForReturns =
  (returnsFlow: ActiveFlow): ThunkAction =>
  async (dispatch: Dispatch, getState: () => State): Promise<void> => {
    const result = await fetchAccount(dispatch);

    if (!result.ok) {
      dispatch(
        getSystemErrorMessaging({ errorCode: 'failedReturnsAccountFetch', serverMsg: result.message || undefined }),
      );
      return;
    }

    // unauthenticatedUser = Identity session but no Avatax account. On offer routes we show
    // error dialog only (redirect would loop). On non-offer routes (e.g. /returns) we redirect to login.
    if (isUnauthenticatedUserResultValue(result.value)) {
      handleUnauthenticatedUser(dispatch, getState, 'failedAccountFetch');
      return;
    }
    clearAuthRedirectLoopGuard();
    dispatch(getPaymentMethods());
    const { obligations } = result.value;

    const returnsEligibilityStatus = getReturnsEligibility(obligations);

    if (returnsEligibilityStatus !== 'ELIGIBLE') {
      dispatch(
        getSystemErrorMessaging({
          errorCode: 'ineligibleForReturnsPurchase',
          activeFlow: returnsFlow,
          returnsEligibilityStatus,
        }),
      );
      return;
    }

    const { accountId, userId, billingContact, companyName } = result.value;
    if (billingContact !== 'ERROR') dispatch(fillSharedForm({ billingContact, companyName }));

    analytics.identify(userId, { accountId });

    const storedConnector = getStoredConnector(accountId, getState);
    dispatch(receiveUser({ ...result.value, storedConnector }));
  };

export const fetchAccountForAvaTax =
  (abortOnPaidTier = true): ThunkAction =>
  async (dispatch: Dispatch, getState: () => State): Promise<void> => {
    const result = await fetchAccount(dispatch);

    if (!result.ok) {
      dispatch(
        getSystemErrorMessaging({
          errorCode: 'failedAccountFetch',
          serverMsg: result.message || undefined,
        }),
      );
      return;
    }

    // unauthenticatedUser = Identity session but no Avatax account. On offer routes we show
    // error dialog only (redirect would loop). On non-offer routes (e.g. /buy) we redirect to login.
    if (isUnauthenticatedUserResultValue(result.value)) {
      handleUnauthenticatedUser(dispatch, getState, 'ineligibleForAccountsPayable');
      return;
    }
    clearAuthRedirectLoopGuard();
    dispatch(getPaymentMethods());
    const { obligations } = result.value;

    const userHasPaidAvatax = obligations.some(isPaidAvataxObligation);

    if (userHasPaidAvatax && abortOnPaidTier) {
      dispatch(getSystemErrorMessaging({ errorCode: 'existingPaidAvatax' }));
      return;
    }

    const { accountId, userId } = result.value;

    analytics.identify(userId, { accountId });

    const storedConnector = getStoredConnector(accountId, getState);
    dispatch(receiveUser({ ...result.value, storedConnector }));
  };

export const verifyEmail = async (email: string, dispatch: Dispatch): Promise<boolean> => {
  const result = await fetch(dispatch)(`${conf.API_URL}/api/accounts/email/verify?email=${email}`);
  return result.ok;
};

export const sendAPMarketingData = async (dispatch: Dispatch, accountInfo: APAccountInfo, companyName: string) => {
  const body = { accountInfo, companyName };

  const result = await fetch(dispatch, { showLoadingWheel: false })(`${conf.API_URL}/api/accounts/ap/marketing-data`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!result.ok) {
    analytics.track('Failed to POST lead to Miles', {
      message: result.message,
      status: result.status,
      body: scrubPostBody(body),
    });
  }

  return result;
};
export const formatObapiPostBody = (state: State): CreateObapiAccountRequest => {
  const {
    account: { retryKey, selectedPaymentMethod, isAdmin },
    sharedSignup: { signupForm },
    data: { marketingData },
    system: { activeFlow },
  } = state;

  return {
    activeFlow,
    offer: getOfferByActiveFlow(state, selectedPaymentMethod?.Id),
    marketingData,
    accountInfo: {
      accountName: signupForm.companyName,
      firstName: signupForm.firstName,
      lastName: signupForm.lastName,
      email: signupForm.email,
      userPassword: signupForm.password || '',
      acceptAvalaraTermsAndConditions: true,
      haveReadAvalaraTermsAndConditions: true,
      phone: signupForm.phone,
      isAdmin,
      companyAddress: {
        line: [signupForm.street1, signupForm.street2].filter(Boolean).join(' '),
        region: signupForm.state,
        postalCode: signupForm.postalCode !== '' ? signupForm.postalCode : 'none',
        city: signupForm.city,
        country: signupForm.country,
      },
      welcomeEmail: 'Suppressed',
    },
    info: getOfferMetadataByActiveFlow(state),
    retryKey,
  };
};
